<!--
  PLACEHOLDER — Income Estimate (demo)

  Per-stream monthly net income table. Renders fine but always in
  compact density (the design includes a regular/compact density toggle
  on the toolbar that isn't wired yet). Stream rows are static; the
  design calls for click-through to a Stream detail drawer once that
  exists.
-->
<template>
  <div class="table-wrap">
    <table class="income-table density-compact">
      <thead>
        <tr class="year-row">
          <th class="label-col" />
          <th
            v-for="(g, gi) in groups"
            :key="g.year"
            :colspan="g.count"
            :class="['year-cell', gi > 0 ? 'year-start' : '']"
          >
            20{{ g.year }}
          </th>
          <th class="year-cell year-cell-total" />
        </tr>
        <tr>
          <th class="label-col">Stream</th>
          <th
            v-for="(m, i) in monthSlice"
            :key="m.key"
            :class="cellCls(i)"
            @mouseenter="hover = i"
            @mouseleave="hover = null"
          >
            {{ m.short }}
          </th>
          <th class="total-col">Total</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="cat in CATEGORIES" :key="cat">
          <tr class="group-row">
            <td class="label-col">
              <span class="stream-name">
                <span
                  class="stream-dot"
                  :style="{ background: CATEGORY_COLORS[cat] }"
                />
                {{ categoryHeading(cat) }}
              </span>
            </td>
            <td
              v-for="(m, i) in monthSlice"
              :key="m.key"
              :class="cellCls(i)"
              :title="cm[cat][i] >= 1 ? '€' + fmt(cm[cat][i]) : undefined"
              @mouseenter="hover = i"
              @mouseleave="hover = null"
            >
              <span v-if="cm[cat][i] < 1" class="cell-zero">—</span>
              <template v-else>€{{ fmtCell(cm[cat][i]) }}</template>
            </td>
            <td class="total-col" :title="'€' + fmt(categoryTotal(cat))">
              €{{ fmtK(categoryTotal(cat)) }}
            </td>
          </tr>
          <tr
            v-for="s in streamsForCategory(cat)"
            :key="s.id"
            class="stream-row"
          >
            <td class="label-col">
              {{ s.name }}
              <span :class="['cert-pip', s.certainty]">
                {{ certPipLabel(s.certainty) }}
              </span>
              <span class="stream-meta">{{ s.subtitle }}</span>
            </td>
            <td
              v-for="(m, i) in monthSlice"
              :key="m.key"
              :class="streamCellCls(i, s.certainty, s.net[i])"
              :title="s.net[i] >= 1 ? '€' + fmt(s.net[i]) : undefined"
              @mouseenter="hover = i"
              @mouseleave="hover = null"
            >
              <span v-if="s.net[i] < 1" class="cell-zero">—</span>
              <template v-else>€{{ fmtCell(s.net[i]) }}</template>
            </td>
            <td
              class="total-col stream-total-col"
              :title="'€' + fmt(streamTotal(s))"
            >
              €{{ fmtK(streamTotal(s)) }}
            </td>
          </tr>
        </template>
        <tr class="totals">
          <td class="label-col ps-2">Net total</td>
          <td
            v-for="(m, i) in monthSlice"
            :key="m.key"
            :class="cellCls(i)"
            :title="monthTotal(i) >= 1 ? '€' + fmt(monthTotal(i)) : undefined"
            @mouseenter="hover = i"
            @mouseleave="hover = null"
          >
            <span v-if="monthTotal(i) < 1" class="cell-zero">—</span>
            <template v-else>€{{ fmtCell(monthTotal(i)) }}</template>
          </td>
          <td :title="'€' + fmt(grandTotal)">€{{ fmtK(grandTotal) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  CATEGORIES,
  CATEGORY_COLORS,
  categoryMonthly,
  fmt,
  fmtCell,
  fmtK,
  MONTHS,
  monthsForHorizon,
  type Category,
  type Certainty,
  type HorizonValue,
  type Stream,
} from "./incomeEstimateDemoData";

const props = defineProps<{
  horizon: HorizonValue;
  streams: Stream[];
}>();

const hover = defineModel<number | null>("hover", { required: true });

const monthCount = computed(() => monthsForHorizon(props.horizon));
const monthSlice = computed(() => MONTHS.slice(0, monthCount.value));
const cm = computed(() => categoryMonthly(props.streams));

const groups = computed(() => {
  const acc: { year: string; start: number; end: number; count: number }[] = [];
  monthSlice.value.forEach((m, i) => {
    const last = acc[acc.length - 1];
    if (last && last.year === m.year) {
      last.count++;
      last.end = i;
    } else {
      acc.push({ year: m.year, start: i, end: i, count: 1 });
    }
  });
  return acc;
});

const isYearStart = (i: number) =>
  i > 0 && monthSlice.value[i - 1].year !== monthSlice.value[i].year;

const cellCls = (i: number): string => {
  const parts: string[] = [];
  if (i === hover.value) parts.push("col-hover");
  if (isYearStart(i)) parts.push("year-start");
  return parts.join(" ");
};

const streamCellCls = (i: number, certainty: Certainty, v: number): string => {
  const parts: string[] = [];
  if (i === hover.value) parts.push("col-hover");
  if (isYearStart(i)) parts.push("year-start");
  if (certainty !== "fixed" && v >= 1) parts.push("cell-uncertain");
  return parts.join(" ");
};

const certPipLabel = (certainty: Certainty) =>
  certainty === "fixed"
    ? "Locked"
    : certainty === "estimated"
      ? "Estimate"
      : "Spot";

const categoryHeading = (cat: Category) =>
  cat === "PPA"
    ? "PPA deals"
    : cat === "Forward"
      ? "Forward sales"
      : "Spot sales";

const streamsForCategory = (cat: Category) =>
  props.streams.filter((s) => s.category === cat);

const categoryTotal = (cat: Category) => {
  let sum = 0;
  for (let i = 0; i < monthCount.value; i++) sum += cm.value[cat][i];
  return sum;
};

const streamTotal = (s: Stream) => {
  let sum = 0;
  for (let i = 0; i < monthCount.value; i++) sum += s.net[i];
  return sum;
};

const monthTotal = (i: number) =>
  CATEGORIES.reduce((acc, c) => acc + cm.value[c][i], 0);

const grandTotal = computed(() => {
  let sum = 0;
  for (let i = 0; i < monthCount.value; i++) sum += monthTotal(i);
  return sum;
});
</script>

<style scoped lang="scss">
/* All table styles live here (sticky label column, hover-column highlight,
   group + totals rows, year dividers, certainty pip). The table is dense
   enough that no Bootstrap .table modifier matches — Bootstrap's spacing
   scale is too generous for the 24-column monthly grid. */
.table-wrap {
  overflow-x: auto;
  margin: 0 -22px;
  padding: 0 22px;
}
.income-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-variant-numeric: tabular-nums;
  font-size: 12px;

  &.density-compact {
    th,
    td {
      padding: 5px 4px;
      font-size: 11.5px;
      text-align: right;
      border-bottom: 1px solid var(--phoenix-gray-200);
      white-space: nowrap;
    }
  }

  th.label-col,
  td.label-col {
    text-align: left;
    font-weight: 500;
    color: var(--phoenix-emphasis-color);
    padding-left: 0;
    position: sticky;
    left: 0;
    background: var(--phoenix-white);
    z-index: 1;
  }

  thead th {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--phoenix-gray-500);
    border-bottom: 1px solid var(--phoenix-gray-300);
    padding-top: 4px;
    padding-bottom: 8px;
  }

  .total-col {
    background: rgba(var(--phoenix-primary-rgb), 0.04);
  }
  .stream-total-col {
    font-weight: 500;
  }

  .group-row td {
    background: var(--phoenix-gray-50);
    font-weight: 600;
    color: var(--phoenix-emphasis-color);
    padding-top: 8px;
    padding-bottom: 8px;

    &.label-col {
      background: var(--phoenix-gray-50);
    }
  }

  .stream-row td.label-col {
    padding-left: 14px;
    font-weight: 400;
    color: var(--phoenix-emphasis-color);
    font-size: 12.5px;
  }

  tr.totals td {
    background: #0d2218;
    color: #fff;
    font-weight: 600;
    border-bottom: 0;
    padding-top: 9px;
    padding-bottom: 9px;

    &.label-col {
      background: #0d2218;
      color: #fff;
    }
  }

  .cell-zero {
    color: var(--phoenix-gray-500);
  }

  /* Column-hover highlight, synced with chart bar hover */
  th.col-hover,
  td.col-hover {
    background: rgba(var(--phoenix-primary-rgb), 0.06);
  }
  tr.group-row td.col-hover {
    background: rgba(var(--phoenix-primary-rgb), 0.1);
  }
  tr.totals td.col-hover {
    background: var(--phoenix-primary-text-emphasis);
  }

  tr.year-row th {
    background: var(--phoenix-gray-50);
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: none;
    color: var(--phoenix-emphasis-color);
    font-variant-numeric: tabular-nums;
    padding: 7px 6px 5px;
    border-bottom: 1px solid var(--phoenix-gray-300);

    &.label-col {
      background: var(--phoenix-white);
      border-bottom: 1px solid var(--phoenix-gray-300);
    }
    &.year-cell-total {
      background: rgba(var(--phoenix-primary-rgb), 0.04);
    }
    &.year-start {
      border-left: 1px solid var(--phoenix-gray-400);
    }
  }

  th.year-start,
  td.year-start {
    border-left: 1px solid var(--phoenix-gray-300);
  }

  .cell-uncertain {
    color: var(--phoenix-warning-text-emphasis);
    font-style: italic;

    &::after {
      content: "≈";
      margin-left: 3px;
      color: var(--phoenix-warning);
      font-size: 10px;
      vertical-align: 0.14em;
      font-style: normal;
    }
  }

  .stream-meta {
    display: block;
    font-size: 10.5px;
    color: var(--phoenix-tertiary-color);
    font-weight: 400;
    margin-top: 1px;
    letter-spacing: 0;
    text-transform: none;
  }
  .stream-name {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .stream-dot {
    width: 7px;
    height: 7px;
    border-radius: 2px;
    flex: 0 0 7px;
  }
}

/* Certainty pip — renders alongside the stream name */
.cert-pip {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 2px;
  margin-left: 4px;

  &.fixed {
    background: rgba(var(--phoenix-primary-rgb), 0.1);
    color: var(--phoenix-primary-text-emphasis);
  }
  &.estimated {
    background: rgba(var(--phoenix-warning-rgb), 0.14);
    color: var(--phoenix-warning-text-emphasis);
  }
  &.speculative {
    background: rgba(var(--phoenix-warning-rgb), 0.2);
    color: var(--phoenix-warning-text-emphasis);
  }
}
</style>
